import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ActionIcon,
  Button,
  Group,
  Select,
  Tooltip,
} from "@mantine/core";
import { useDisclosure, useDocumentTitle } from "@mantine/hooks";
import { IconPlus, IconRefresh } from "@tabler/icons-react";
import { useIsFetching, useQueryClient } from "@tanstack/react-query";

import { WithAppointmentsNavbar } from "@layouts/WithAppointmentsNavbar";
import { WithSidebar } from "@layouts/WithSidebar";

import { PermissionGate } from "@components/PermissionGate";
import { TabsContent, TabsList, TabsRoot } from "@components/Tabs";

import { PAGE_TITLE, ROLES } from "@constants/index";

import { masterclassQKeys } from "@hooks/queries/masterclass/queryKeys";
import { useGetMasterclasses } from "@hooks/queries/masterclass/useGetMasterclasses";
import { useGetUsers } from "@hooks/queries/user/useGetUsers";
import { useRefreshAnimation } from "@hooks/useRefreshAnimation";

import { useAuth } from "@contexts/auth";

import { Masterclass as MasterclassType } from "@customTypes/masterclass";

import { StyledIncoming } from "../Incoming/Incoming.styled";

import { EnrollmentTable } from "./components/EnrollmentTable";
import { MasterclassModal } from "./components/MasterclassModal";
import { MasterclassTable } from "./components/MasterclassTable";


export const Masterclass = () => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { roles, id: userId } = useAuth();
  
  const isAdmin = roles.includes(ROLES.ADMIN) || roles.includes(ROLES.RECEPTIONIST);
  const isDoctor = roles.includes(ROLES.DOCTOR);

  useDocumentTitle(`${t("masterclass")} • ${PAGE_TITLE}`);

  const [activeTab, setActiveTab] = useState<string>("classes");
  const [providerFilter, setProviderFilter] = useState<string | null>(isDoctor ? userId : null);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [selectedMasterclass, setSelectedMasterclass] = useState<MasterclassType | null>(null);

  const [opened, { open, close }] = useDisclosure(false);

  const handleEdit = (masterclass: MasterclassType) => {
    setSelectedMasterclass(masterclass);
    open();
  };

  const handleAdd = () => {
    setSelectedMasterclass(null);
    open();
  };

  // Fetch providers for filter (Admin only)
  const { data: providersData } = useGetUsers({ 
    role: "doctor"
  });

  const providersOptions = useMemo(() => {
    return (providersData || []).map(p => ({
      label: p.fullName,
      value: p.id,
    }));
  }, [providersData]);

  // Fetch masterclasses
  const { 
    data: masterclasses, 
    isLoading: isClassesLoading, 
    isRefetching: isClassesRefetching,
    isError: isClassesError,
    isSuccess: isClassesSuccess
  } = useGetMasterclasses({
    provider_id: providerFilter || undefined
  });

  const isFetchingEnrollments = useIsFetching({ queryKey: masterclassQKeys.enrollments() });
  const { buttonRef } = useRefreshAnimation({ isRefreshing: activeTab === "classes" ? isClassesRefetching : isFetchingEnrollments > 0 });

  useEffect(() => {
    if (isClassesSuccess && selectedClassId && !masterclasses?.some(item => item._id === selectedClassId)) {
      setSelectedClassId(null);
    }
  }, [masterclasses, isClassesSuccess, selectedClassId]);

  const masterclassOptions = useMemo(() => {
    return (masterclasses || []).map(m => ({
      label: m.name,
      value: m._id
    }));
  }, [masterclasses]);

  return (
    <WithSidebar hasBackground hasDashboardNavbar>
      <WithAppointmentsNavbar>
        <StyledIncoming>
          <TabsRoot
            value={activeTab}
            onValueChange={val => {
              setActiveTab(val);
            }}>
            <div className="nested-header" style={{ padding: '0 2rem' }}>
              <Group>
                <TabsList
                  ariaLabel="Masterclass Type"
                  comboboxItems={[
                    { label: t("masterclass"), value: "classes" },
                    { label: t("enrollments"), value: "enrollments" },
                  ]}
                />
                <Tooltip label={t("refresh-data")}>
                  <ActionIcon
                    ref={buttonRef}
                    variant="default"
                    size="lg"
                    onClick={() => {
                      queryClient.invalidateQueries(masterclassQKeys.all());
                    }}>
                    <IconRefresh size={16} />
                  </ActionIcon>
                </Tooltip>
              </Group>

              <div className="wrapper">
                {isAdmin && (
                  <Select
                    placeholder={t("filter-by-provider")}
                    data={providersOptions}
                    value={providerFilter}
                    onChange={setProviderFilter}
                    clearable
                    searchable
                    w={200}
                  />
                )}

                {activeTab === "enrollments" && (
                  <Select
                    placeholder={t("select-masterclass")}
                    data={masterclassOptions}
                    value={selectedClassId}
                    onChange={setSelectedClassId}
                    clearable
                    searchable
                    w={250}
                  />
                )}

                <MasterclassModal 
                  opened={opened} 
                  close={close} 
                  masterclass={selectedMasterclass} 
                />
                <PermissionGate permission="resources.create"><Button leftSection={<IconPlus size={18} />} onClick={handleAdd}>
                  {t("add-masterclass")}
                </Button></PermissionGate>
              </div>
            </div>

            <div style={{ padding: '0 2rem' }}>
              <TabsContent value="classes">
                <MasterclassTable 
                  data={masterclasses}
                  isLoading={isClassesLoading}
                  isError={isClassesError}
                  isSuccess={isClassesSuccess}
                  isRefetching={isClassesRefetching}
                  onEdit={handleEdit}
                  onViewEnrollments={item => { setSelectedClassId(item._id); setActiveTab("enrollments"); }}
                />
              </TabsContent>
              
              <TabsContent value="enrollments">
                <EnrollmentTable 
                  key={selectedClassId || "none"}
                  masterclassId={selectedClassId}
                  masterclass={masterclasses?.find(item => item._id === selectedClassId)}
                />
              </TabsContent>
            </div>
          </TabsRoot>
        </StyledIncoming>
      </WithAppointmentsNavbar>
    </WithSidebar>
  );
};
